<template>
  <div v-if="site" class="p-6">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-semibold flex items-center dark:text-white">
        <button
          @click="goBack"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Back to Sites
        </button>
        Site Details: {{ site.name }}
      </h1>
      <div class="relative group">
        <button type="button" class="hover:bg-transparent bg-transparent" @click="startTutorial">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
        <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </div>

    <!-- Tab Navigation -->
    <TabNavigation id="site-detail-tabs" :activeTab="activeTab" @change-tab="onChangeTab" />

    <!-- Tabs Content -->
    <div id="site-detail-content" class="mt-4">
      <component
        :is="currentTabComponent"
        :site="site"
        :displayFields="visibleFields"
        :tunnels="tunnels"
        :tunnelsLoaded="tunnelsLoaded"
        :pendingChanges="pendingChanges"
        :errors="fieldErrors"
        @update-site="handleUpdateSite"
        @update-tunnel="handleUpdateTunnel"
      />

      <!-- SNMP Tab -->
      <SNMPTab
        v-if="permsLoaded"
        v-show="activeTab === 'snmp'"
        parent-type="site"
        :parent-id="site.id"
        :org-id="site.org"
      />
    </div>
  </div>

  <!-- Error Message -->
  <PageError v-if="errorMessage"
    :error="errorMessage"
    :errorCode="errorCode" 
  />

</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import TabNavigation from "@/components/SiteDetail/TabNavigation.vue";
import SiteDetailsTab from "@/components/SiteDetail/SiteDetailsTab.vue";
import PendingChangesTab from "@/components/SiteDetail/PendingChangesTab.vue";
import TunnelsTab from "@/components/SiteDetail/TunnelsTab.vue";
import GrafanaDashboardTab from "@/components/SiteDetail/GrafanaDashboardTab.vue";
import ExplainConfigButton from "@/components/ExplainConfigButton.vue";
import SNMPTab from "@/components/SiteDetail/SnmpTab.vue";

// Import your services or API calls
import { getSites, updateSite } from "@/services/siteService";
import { getAllOrgs } from "@/services/orgService";
import { getAllDivisions } from "@/services/divisionService";
import { getTunnelsBySite, updateTunnel } from "@/services/tunnelService";
import axios from "axios";

import { formatError } from "@/utils/errorMessages";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useTutorial } from "@/composables/tutorial";

const tenant = useMultiTenant();
const route = useRoute();
const router = useRouter();

const site = ref(null);
const fieldErrors = ref({});
const activeTab = ref("siteDetails");
const displayFields = ref([
  { key: "name", label: "Site Name", type: "text" },
  { key: "location", label: "Location", type: "text" },
  { key: "api_key", label: "API Key", type: "password" },
  { key: "api_address", label: "API Address", type: "text" },
  { key: "description", label: "Description", type: "text" },
  {
    key: "mgmt_loopback_address",
    label: "Management IP Address",
    type: "text",
  },
  { key: "code", label: "Site Code", type: "text" },
  {
    key: "description_abbreviate",
    label: "Abbreviated Description",
    type: "text",
  },
  { key: "country", label: "Country", type: "select", options: [] },
  { key: "region", label: "Region", type: "select", options: [] },
  { key: "org", label: "Organisation", type: "select", options: [] },
  { key: "division", label: "Division", type: "select", options: [] },
  { key: "in_maintenance", label: "In Maintenance", type: "checkbox" },
  {
    key: "mgmt_loopback_address_port",
    label: "Management IP Address Port",
    type: "number",
  },
  { key: "scrape_interval", label: "Scrape Interval", type: "number", staffOnly: true },
  { key: "scrape_timeout", label: "Scrape Timeout", type: "number", staffOnly: true },
  { key: "device_name", label: "Device Name", type: "text" },
  { key: "geohash", label: "Geohash", type: "text" },
  { key: "created_date", label: "Created Date", type: "text" },
  { key: "last_modified", label: "Last Modified", type: "text" },
]);

const visibleFields = computed(() =>
  displayFields.value.filter(f => !f.staffOnly || useStore.isStaff())
);

const tunnels = ref([]);
const tunnelsLoaded = ref(false);
const pendingChanges = ref({});
const organizations = ref({});
const divisions = ref([]);
const apiKeyVisible = ref(false);

const parsedPendingSite = ref(null);
const parsedOriginalSite = ref(null);

const errorMessage = ref(null);
const errorCode = ref(null);
const organizationsList = ref([]);

const countryChoices = {
  ZA: "South Africa",
  BW: "Botswana",
  NA: "Namibia",
  SZ: "Swaziland",
  ZM: "Zimbabwe",
  LS: "Lesotho",
  ET: "Ethiopia",
  KN: "Kenya",
  NG: "Nigeria",
  SH: "Saint Helena"
};

const regionChoices = {
  ZA: [
    { code: "EC", name: "Eastern Cape" },
    { code: "FS", name: "Free State" },
    { code: "GT", name: "Gauteng" },
    { code: "NL", name: "KwaZulu-Natal" },
    { code: "LP", name: "Limpopo" },
    { code: "MP", name: "Mpumalanga" },
    { code: "NC", name: "Northern Cape" },
    { code: "NW", name: "North-West" },
    { code: "WC", name: "Western Cape" },
  ],
  BW: [
    { code: "CE", name: "Central" },
    { code: "CH", name: "Chobe" },
    { code: "GH", name: "Ghanzi" },
    { code: "KG", name: "Kgalagadi" },
    { code: "KL", name: "Kgatleng" },
    { code: "KW", name: "Kweneng" },
    { code: "NE", name: "North-East" },
    { code: "NW", name: "North-West" },
    { code: "SE", name: "South-East" },
    { code: "SO", name: "Southern" },
  ],
  NA: [
    { code: "ER", name: "Erongo" },
    { code: "HA", name: "Hardap" },
    { code: "KA", name: "Karas" },
    { code: "KE", name: "Kavango East" },
    { code: "KW", name: "Kavango West" },
    { code: "KH", name: "Khomas" },
    { code: "KU", name: "Kunene" },
    { code: "OW", name: "Ohangwena" },
    { code: "OH", name: "Omaheke" },
    { code: "OS", name: "Omusati" },
    { code: "ON", name: "Oshana" },
    { code: "OT", name: "Oshikoto" },
    { code: "OD", name: "Otjozondjupa" },
    { code: "CA", name: "Zambezi" },
  ],
  SZ: [
    { code: "HH", name: "Hhohho" },
    { code: "LU", name: "Lubombo" },
    { code: "MA", name: "Manzini" },
    { code: "SH", name: "Shiselweni" },
  ],
  ZM: [
    { code: "BU", name: "Bulawayo" },
    { code: "HA", name: "Harare" },
    { code: "MA", name: "Manicaland" },
    { code: "MC", name: "Mashonaland Central" },
    { code: "ME", name: "Mashonaland East" },
    { code: "MW", name: "Mashonaland West" },
    { code: "MV", name: "Masvingo" },
    { code: "MN", name: "Matabeleland North" },
    { code: "MS", name: "Matabeleland South" },
    { code: "MI", name: "Midlands" },
  ],
  LS: [
    { code: "BB", name: "Butha-Buthe" },
    { code: "LE", name: "Leribe" },
    { code: "BE", name: "Berea" },
    { code: "MS", name: "Maseru" },
    { code: "MF", name: "Mafeteng" },
    { code: "MH", name: "Mohales Hoek" },
    { code: "QT", name: "Quthing" },
    { code: "QN", name: "Qachas Nek" },
    { code: "MK", name: "Mokhotlong" },
    { code: "TT", name: "Thaba Tseka" },
  ],
  ET: [
    { code: "AA", name: "Addis Ababa" },
    { code: "AF", name: "Afar" },
    { code: "AM", name: "Amhara" },
    { code: "BG", name: "Benishanul-Gumuz" },
    { code: "CE", name: "Centra Ethiopia" },
    { code: "DD", name: "Dire Dawa" },
    { code: "GB", name: "Gambela" },
    { code: "HR", name: "Harari" },
    { code: "OR", name: "Oromia" },
    { code: "SD", name: "Sidama" },
    { code: "SM", name: "Somali" },
    { code: "SR", name: " South Ethiopia" },
    { code: "SW", name: "South West Ethiopia" },
    { code: "TG", name: "Tigray" },
  ],
  NG: [
    { code: "AB", name: "Abia" },
    { code: "AD", name: "Adamawa" },
    { code: "AK", name: "Akwa Ibom" },
    { code: "AN", name: "Anambra" },
    { code: "BA", name: "Bauchi" },
    { code: "BY", name: "Bayelsa" },
    { code: "BE", name: "Benue" },
    { code: "BO", name: "Borno" },
    { code: "CR", name: "Cross River" },
    { code: "DE", name: "Delta" },
    { code: "EB", name: "Ebonyi" },
    { code: "ED", name: "Edo" },
    { code: "EK", name: "Ekiti" },
    { code: "EN", name: "Enugu" },
    { code: "FC", name: "Federal Capital Territory" },
    { code: "GO", name: "Gombe" },
    { code: "IM", name: "Imo" },
    { code: "JI", name: "Jigawa" },
    { code: "KD", name: "Kaduna" },
    { code: "KN", name: "Kano" },
    { code: "KT", name: "Katsina" },
    { code: "KE", name: "Kebbi" },
    { code: "KO", name: "Kogi" },
    { code: "KW", name: "Kwara" },
    { code: "LA", name: "Lagos" },
    { code: "NA", name: "Nasarawa" },
    { code: "NI", name: "Niger" },
    { code: "OG", name: "Ogun" },
    { code: "ON", name: "Ondo" },
    { code: "OS", name: "Osun" },
    { code: "OY", name: "Oyo" },
    { code: "PL", name: "Plateau" },
    { code: "RI", name: "Rivers" },
    { code: "SO", name: "Sokoto" },
    { code: "TA", name: "Taraba" },
    { code: "YO", name: "Yobe" },
    { code: "ZA", name: "Zamfara" },
  ],
  KN: [
    { code: "MB", name: "Mombasa" },
    { code: "KW", name: "Kwale" },
    { code: "KL", name: "Kilifi" },
    { code: "TR", name: "Tana River" },
    { code: "LM", name: "Lamu" },
    { code: "TT", name: "Taita-Taveta" },
    { code: "GR", name: "Garissa" },
    { code: "WJ", name: "Wajir" },
    { code: "MD", name: "Mandera" },
    { code: "MS", name: "Marsabit" },
    { code: "IS", name: "Isiolo" },
    { code: "ME", name: "Meru" },
    { code: "TN", name: "Tharaka-Nithi" },
    { code: "EM", name: "Embu" },
    { code: "KT", name: "Kitui" },
    { code: "MH", name: "Machakos" },
    { code: "MK", name: "Makueni" },
    { code: "ND", name: "Nyandaru" },
    { code: "NY", name: "Nyeri" },
    { code: "KR", name: "Kirinyaga" },
    { code: "MR", name: "Murang'a" },
    { code: "KM", name: "Kiambu" },
    { code: "TK", name: "Turkana" },
    { code: "WP", name: "West Pokot" },
    { code: "SB", name: "Samburu" },
    { code: "TA", name: "Trans-Nzoia" },
    { code: "UG", name: "Uasin Gishu" },
    { code: "EL", name: "Elgeyo-Marakwet" },
    { code: "NN", name: "Nandi" },
    { code: "BR", name: "Baringo" },
    { code: "LK", name: "Laikipia" },
    { code: "NK", name: "Nakuru" },
    { code: "NR", name: "Narok" },
    { code: "KJ", name: "Kajiado" },
    { code: "KC", name: "Kericho" },
    { code: "BO", name: "Bornet" },
    { code: "KK", name: "Kakamega" },
    { code: "VH", name: "Vihiga" },
    { code: "BU", name: "Bungoma" },
    { code: "BS", name: "Busia" },
    { code: "SY", name: "Siaya" },
    { code: "KI", name: "Kisumu" },
    { code: "HB", name: "Homa Bay" },
    { code: "MI", name: "Migori" },
    { code: "KS", name: "Kisii" },
    { code: "NM", name: "Nyamira" },
    { code: "NB", name: "Nairobi" },
  ],
  SH: [
    { code: "AC", name: "Ascension Island" },
    { code: "SHI", name: "Saint Helena" },
    { code: "TR", name: "Tristan da Cunha" },
  ],
};

const currentTabComponent = computed(() => {
  return {
    siteDetails: SiteDetailsTab,
    tunnels: TunnelsTab,
    dashboard: GrafanaDashboardTab,
  }[activeTab.value];
});

const onChangeTab = (tabKey) => {
  activeTab.value = tabKey;
};

const fetchSiteDetails = async () => {
  try {
    const validation = tenant.validateSchemaForOperation();

   if (!validation.valid ) {
      errorMessage.value = "Malformed request. Failed to fetch site details!";
      errorCode.value = "422!";
      return;
    }

    const sites = await getSites(validation.schemaName);

    const siteId = parseInt(route.params.id);
    site.value = sites.find((s) => s.id === siteId);
    updateFieldOptions();
    parseSiteData();
    parsePendingSite();
  } catch (error) {
    console.error("Error fetching site details:", error);
    const { code, message } = formatError(error);
    errorCode.value = code;
    errorMessage.value = message;
  }
};

const fetchOrganizations = async () => {
  try {
    const orgs = await getAllOrgs();
    organizations.value = orgs.reduce((acc, org) => {
      acc[org.id] = org.name;
      return acc;
    }, {});
    updateFieldOptions();
  } catch (error) {
    console.error("Error fetching organizations:", error);
  }
};

const fetchDivisions = async (schemaName) => {
  try {
    const validation = tenant.validateSchemaForOperation();
    if ( !validation.valid ) {
      errorMessage.value = "Malformed request. Failed to fetch divisions!";
      errorCode.value = "422!";
      return;
    }
    divisions.value = await getAllDivisions(validation.schemaName);

    updateFieldOptions();
  } catch (error) {
    console.error("Error fetching divisions:", error);
  }
};

const fetchTunnels = async () => {
  try {
    const validation = tenant.validateSchemaForOperation();
   
    if (!validation.valid) {
      errorMessage.value = "Malformed request. Failed to fetch tunnels!";
      errorCode.value = "422!";
      return;
    }
    tunnelsLoaded.value = false; // Reset loading state

    tunnels.value = await getTunnelsBySite(route.params.id, validation.schemaName);
    tunnelsLoaded.value = true;
  } catch (error) {
    console.error("Error fetching tunnels:", error);
    tunnels.value = [];
    tunnelsLoaded.value = true;
  }
};

const handleUpdateSite = async (updatedFields) => {
  try {
    // Merge the new changes with existing pending changes
    let pendingSite = { ...parsedPendingSite.value, ...updatedFields };

    // Ensure org and division are numbers
    pendingSite.org = parseInt(pendingSite.org) || site.value.org;
    pendingSite.division =
      parseInt(pendingSite.division) || site.value.division;

    // Ensure all required fields are present
    const requiredFields = [
      "name",
      "location",
      "api_key",
      "description",
      "mgmt_loopback_address",
      "code",
      "description_abbreviate",
      "country",
      "region",
      "org",
      "division",
      "in_maintenance",
      "mgmt_loopback_address_port",
      "scrape_interval",
      "scrape_timeout",
      "device_name",
    ];

    // Fill in any missing required fields from the current site data
    requiredFields.forEach((field) => {
      if (!pendingSite.hasOwnProperty(field)) {
        pendingSite[field] = site.value[field];
      }
    });

    // Ensure proper data types
    pendingSite.in_maintenance = Boolean(pendingSite.in_maintenance);
    pendingSite.mgmt_loopback_address_port =
      parseInt(pendingSite.mgmt_loopback_address_port) || 0;
    pendingSite.scrape_interval = parseInt(pendingSite.scrape_interval) || 0;
    pendingSite.scrape_timeout = parseInt(pendingSite.scrape_timeout) || 0;

    // check if staff user and schema is provided
    const validation = tenant.validateSchemaForOperation();
  
    if (!validation.valid ) {
      errorMessage.value = "Malformed request. Failed to update site details!";
      errorCode.value = "422!";
      return;
    }

    // Use the service method
    const updatedSiteData = await updateSite(site.value.id, pendingSite, validation.schemaName);

    // Update the local site data with the response from the server
    site.value = updatedSiteData;
    parsedPendingSite.value = pendingSite;
    compareSites();
    return { success: true };
  } catch (error) {
    fieldErrors.value = {}; 
    const items = displayFields.value
    const errData = error?.response?.data;

    items.forEach(element => {
      const fieldError = errData?.[element.key];
      if (fieldError?.[0]) {
        fieldErrors.value[element.key] = fieldError[0]; 
      }
    });
    return { error: error.message };
  }
};

const handleUpdateTunnel = async (updatedTunnel) => {
  try {
    const validation = tenant.validateSchemaForOperation();
    if (!validation.valid) {
      errorMessage.value = "Malformed request. Failed to update tunnel!";
      errorCode.value = "422!";
      return;
    }

    // Make the API call to update the tunnel on the server
    await updateTunnel(updatedTunnel.id, updatedTunnel, validation.schemaName);

    // Update the tunnel data locally
    const index = tunnels.value.findIndex((t) => t.id === updatedTunnel.id);
    if (index !== -1) {
      tunnels.value.splice(index, 1, updatedTunnel);
    }
  } catch (error) {
    console.error("Error updating tunnel:", error);
    // Optionally, show an error message to the user
  }
};

const goBack = () => {
  router.push({ 
    name: "sites",
    query: tenant.currentSchemaName.value
  });
};

const updateFieldOptions = () => {
  // Update country options
  const countryField = displayFields.value.find((f) => f.key === "country");
  countryField.options = Object.entries(countryChoices).map(
    ([value, label]) => ({
      value,
      label,
    })
  );

  // Update region options based on selected country
  const regionField = displayFields.value.find((f) => f.key === "region");
  if (site.value) {
    regionField.options =
      regionChoices[site.value.country]?.map((r) => ({
        value: r.code,
        label: r.name,
      })) || [];
  }

  // Update organization options
  const orgField = displayFields.value.find((f) => f.key === "org");
  orgField.options = Object.entries(organizations.value).map(
    ([value, label]) => ({
      value: parseInt(value),
      label,
    })
  );

  // Update division options
  const divisionField = displayFields.value.find((f) => f.key === "division");
  divisionField.options = divisions.value.map((d) => ({
    value: d.id,
    label: d.name,
  }));
};

const parseJsonField = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return JSON.parse(value);
  return value; // already a parsed object (JSONField from API)
};

const parseSiteData = () => {
  if (site.value) {
    try {
      parsedPendingSite.value = site.value.pendingsite
        ? parseJsonField(site.value.pendingsite)
        : null;
      parsedOriginalSite.value = site.value.orginalsite
        ? parseJsonField(site.value.orginalsite)
        : null;
      compareSites();
    } catch (error) {
      console.error("Error parsing site data:", error);
      parsedPendingSite.value = null;
      parsedOriginalSite.value = null;
      pendingChanges.value = {};
    }
  } else {
    parsedPendingSite.value = null;
    parsedOriginalSite.value = null;
    pendingChanges.value = {};
  }
};

const compareSites = () => {
  pendingChanges.value = {};
  if (parsedPendingSite.value && parsedOriginalSite.value) {
    for (const [key, value] of Object.entries(parsedPendingSite.value)) {
      if (
        JSON.stringify(value) !== JSON.stringify(parsedOriginalSite.value[key])
      ) {
        pendingChanges.value[key] = {
          original: parsedOriginalSite.value[key],
          pending: value,
        };
      }
    }
  }
};

const parsePendingSite = () => {
  if (site.value && site.value.pendingsite) {
    try {
      parsedPendingSite.value = parseJsonField(site.value.pendingsite);
    } catch (error) {
      console.error("Error parsing pending site:", error);
      parsedPendingSite.value = null;
    }
  } else {
    parsedPendingSite.value = null;
  }
};

const permsLoaded = computed(() => useStore.permissionsLoadedRef.value);

const { start: _start } = useTutorial();
const siteDetailSteps = [
  { element: '#site-detail-tabs', action: 'display', message: 'Use these tabs to navigate between Site Details, Tunnels, Dashboard, and SNMP configuration.' },
  { element: '#site-detail-content', action: 'display', message: 'This section shows the site\'s editable fields. Click any field to update it.' },
];
const startTutorial = async () => { await _start(siteDetailSteps); };

watch(
  () => useStore.getUser(),
  async (newUser) => {
    if (newUser) {
      await fetchOrganizations();
      await fetchSiteDetails();
      await fetchDivisions();
      await fetchTunnels();
    }
  },
  {immediate : true}
);
</script>
